import { api } from "../../lib/api";

// CRM config
export const getCrmConfig = async () => (await api.get("/crm/config")).data;
export const saveCrmConfig = async (cfg) => (await api.put("/crm/config", cfg)).data;

// Stats
export const getCrmStats = async () => (await api.get("/crm/stats")).data;

// Leads
export const getLeads = async () => (await api.get("/crm/leads")).data;
export const createLead = async (lead) => (await api.post("/crm/leads", lead)).data;
export const updateLead = async (id, patch) => (await api.put(`/crm/leads/${id}`, patch)).data;
export const deleteLead = async (id) => (await api.delete(`/crm/leads/${id}`)).data;
export const convertLead = async (id) => (await api.post(`/crm/leads/${id}/convert`)).data;
export const importLeads = async (rows) => (await api.post("/crm/leads/import", { rows })).data;

// Title-case an ALL-CAPS (or messy) name → "Abizer Sabuwala". Common finance
// acronyms are preserved (e.g. HUF, LLP, Pvt Ltd).
const _ACRONYMS = new Set(["HUF", "LLP", "PVT", "LTD", "INC", "LLC", "NRI", "AIF", "PMS", "MF", "SIP"]);
export const toTitleCase = (s) =>
    (s || "")
        .toLowerCase()
        .split(/\s+/)
        .filter(Boolean)
        .map((w) => (_ACRONYMS.has(w.toUpperCase()) ? w.toUpperCase() : w.charAt(0).toUpperCase() + w.slice(1)))
        .join(" ");

// Work-note tag colors (palette key -> chip classes). Keys mirror WORK_TAG_PALETTE on the backend.
export const WORK_TAG_COLORS = {
    amber: "bg-[#FCEFD9] text-[#8a4f17] border-[#E7C9A8]",
    green: "bg-[#E5F3EC] text-[#0B6B3A] border-[#A7D7BE]",
    blue: "bg-[#E6EEF5] text-[#0B3B60] border-[#B3CBE0]",
    purple: "bg-[#EFE9F7] text-[#5b3a8a] border-[#D2C2EA]",
    rose: "bg-[#FBE9EE] text-[#9c2a4a] border-[#EEC2D0]",
    teal: "bg-[#E2F2F1] text-[#0a6b66] border-[#AEDDDA]",
    orange: "bg-[#FCE9DD] text-[#9c4a1c] border-[#E7C3A8]",
    slate: "bg-[#EBEDEF] text-[#3a4452] border-[#CBD2DA]",
};
export const WORK_TAG_PALETTE = Object.keys(WORK_TAG_COLORS);
export const workTagClasses = (color) => WORK_TAG_COLORS[color] || WORK_TAG_COLORS.slate;

// Google Calendar (follow-up invites with Accept/Decline)
export const calendarStatus = async () => (await api.get("/crm/calendar/status")).data;
export const connectCalendar = async (origin) => (await api.post("/crm/calendar/connect", { origin })).data;
export const disconnectCalendar = async () => (await api.post("/crm/calendar/disconnect")).data;
export const sendCalendarInvite = async (payload) => (await api.post("/crm/calendar/invite", payload)).data;
export const getCalendarEvents = async (entityType, entityId) =>
    (await api.get(`/crm/calendar/events?entity_type=${encodeURIComponent(entityType)}&entity_id=${encodeURIComponent(entityId)}`)).data;

// Clients
export const getClients = async () => (await api.get("/crm/clients")).data;
export const createClient = async (c) => (await api.post("/crm/clients", c)).data;
export const updateClient = async (id, patch) => (await api.put(`/crm/clients/${id}`, patch)).data;
export const deleteClient = async (id) => (await api.delete(`/crm/clients/${id}`)).data;
export const importClients = async (rows) => (await api.post("/crm/clients/import", { rows })).data;export const getBirthdays = async (days = 60, limit = 100) =>
    (await api.get(`/crm/birthdays?days=${days}&limit=${limit}`)).data;
export const getBirthdayEmailConfig = async () => (await api.get("/crm/birthday-email-config")).data;
export const saveBirthdayEmailConfig = async (cfg) => (await api.put("/crm/birthday-email-config", cfg)).data;
export const sendBirthdayDigestNow = async () => (await api.post("/crm/birthday-email/send-now")).data;

// Notes
export const getNotes = async (entityType, entityId) =>
    (await api.get(`/crm/notes?entity_type=${entityType}&entity_id=${entityId}`)).data;
export const createNote = async (note) => (await api.post("/crm/notes", note)).data;
export const updateNote = async (id, patch) => (await api.put(`/crm/notes/${id}`, patch)).data;
export const completeNote = async (id) => (await api.put(`/crm/notes/${id}/complete`)).data;
export const deleteNote = async (id) => (await api.delete(`/crm/notes/${id}`)).data;

// Templates
export const getTemplates = async () => (await api.get("/crm/templates")).data;
export const createTemplate = async (t) => (await api.post("/crm/templates", t)).data;
export const updateTemplate = async (id, patch) => (await api.put(`/crm/templates/${id}`, patch)).data;
export const deleteTemplate = async (id) => (await api.delete(`/crm/templates/${id}`)).data;

// Follow-up email
export const scheduleFollowup = async (payload) => (await api.post("/crm/followup", payload)).data;
export const sendEmailOne = async (payload) => (await api.post("/crm/send-email", payload)).data;
export const sendWhatsappText = async (to, body) => (await api.post("/crm/whatsapp/send-text", { to, body })).data;

// WhatsApp
export const broadcast = async (payload) => (await api.post("/crm/broadcasts", payload)).data;
export const getBroadcasts = async () => (await api.get("/crm/broadcasts")).data;

// wa.me click-to-chat (always works for one-off individual messages)
export const waLink = (phone, text) =>
    `https://wa.me/${(phone || "").replace(/\D/g, "")}?text=${encodeURIComponent(text || "")}`;

// Format any birthday string as "dd mmm yy" (e.g. 07 Jun 90). Handles
// YYYY-MM-DD (date input + computed next_birthday) and DD-MM-YYYY / DD/MM/YYYY.
const _MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export const fmtBday = (value) => {
    if (!value) return "—";
    const s = String(value).trim();
    const iso = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
    if (iso) {
        const [, y, mo, d] = iso;
        return `${String(d).padStart(2, "0")} ${_MON[+mo - 1] || "?"} ${String(y).slice(-2)}`;
    }
    const parts = s.split(/[-/.]/).map((p) => p.trim());
    if (parts.length >= 3) {
        let [a, b, y] = parts;
        let d = +a, mo = +b;
        if (+a <= 12 && +b > 12) { d = +b; mo = +a; }  // mm-dd-yyyy fallback
        return `${String(d).padStart(2, "0")} ${_MON[mo - 1] || "?"} ${String(y).slice(-2)}`;
    }
    return s;
};

// Onboarding
export const getOnbConfig = async () => (await api.get("/crm/onboarding/config")).data;
export const saveOnbConfig = async (cfg) => (await api.put("/crm/onboarding/config", cfg)).data;
export const getOnboarding = async () => (await api.get("/crm/onboarding")).data;
export const getOnboardingStats = async () => (await api.get("/crm/onboarding/stats")).data;
export const createOnboarding = async (rec) => (await api.post("/crm/onboarding", rec)).data;
export const updateOnboarding = async (id, patch) => (await api.put(`/crm/onboarding/${id}`, patch)).data;
export const deleteOnboarding = async (id) => (await api.delete(`/crm/onboarding/${id}`)).data;
